import { alpha, AppBar, Avatar, Box, Button, Container, CssBaseline, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, ThemeProvider, Toolbar, Tooltip, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import Logo from "../images/logo-transparent.webp";
import { Link, router, usePage } from "@inertiajs/react";
import { buildTheme } from "../lib/theme";
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from "react-i18next";
import i18n from "../lib/i18n";
import FlashMessages from "../components/FlashMessages";

type Props = {
    children: ReactNode;
}

type SharedProps = {
    theme: "light" | "dark";
    locale: "en" | "es";
    current_email: string | null;
}

function userInitials(email: string | null) {
    if (!email) return "?"
    return email[0].toUpperCase()
}

export default function HomeLayout({ children }: Props) {
    const { theme: initialTheme, locale: initialLocale, current_email } = usePage<SharedProps>().props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [themeMode, setThemeMode] = useState<"light" | "dark">(initialTheme);
    const [locale, setLocale] = useState<"en" | "es">(initialLocale);
    const { t } = useTranslation();

    const theme = useMemo(() => buildTheme(themeMode), [themeMode]);

    // Sync locale from server prop on mount
    useEffect(() => {
        if (i18n.language !== initialLocale) {
            i18n.changeLanguage(initialLocale)
        }
    }, [initialLocale])


    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleToggleTheme = useCallback(() => {
        const next = themeMode === "light" ? "dark" : "light";
        setThemeMode(next);
        fetch("/theme", {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
            body: JSON.stringify({ theme: next }),
        });
    }, [themeMode]);

    const handleToggleLocale = useCallback(() => {
        const next: "en" | "es" = locale === "en" ? "es" : "en";
        setLocale(next);
        i18n.changeLanguage(next);
        fetch("/locale", {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
            body: JSON.stringify({ locale: next }),
        });
    }, [locale]);

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CssBaseline />
            <AppBar
                color="inherit"
                position="sticky"
                elevation={0}
                sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ gap: 1 }}>
                        {/* Logo */}
                        <IconButton LinkComponent={Link} href="/" sx={{ p: 0.5 }}>
                            <Avatar src={Logo} alt="Logo" sx={{ width: 32, height: 32 }} />
                        </IconButton>

                        {/* Title */}
                        <Typography
                            component="span"
                            variant="subtitle1"
                            noWrap
                            sx={{ fontWeight: "bold", flexGrow: 1, display: { xs: "none", sm: "block" } }}
                        >
                            Full Packing del Caribe
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: "block", sm: "none" } }} />

                        {/* Locale toggle */}
                        <Tooltip title={locale === "en" ? t("nav.switchToSpanish") : t("nav.switchToEnglish")}>
                            <Button
                                onClick={handleToggleLocale}
                                size="small"
                                sx={{ minWidth: 0, px: 1, fontWeight: "bold", fontSize: "0.75rem" }}
                                color="inherit"
                            >
                                {locale === "en" ? "ES" : "EN"}
                            </Button>
                        </Tooltip>

                        {/* Theme toggle */}
                        <Tooltip title={themeMode === "dark" ? t("nav.lightMode") : t("nav.darkMode")}>
                            <IconButton onClick={handleToggleTheme} size="small">
                                {themeMode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>

                        {/* User avatar */}
                        <Tooltip title={current_email ?? "Menu"}>
                            <IconButton onClick={handleOpenMenu} size="small">
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: 14,
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                                        color: "primary.main",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {userInitials(current_email)}
                                </Avatar>
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleCloseMenu}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            keepMounted
                            slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
                        >
                            {/* Email header */}
                            {current_email && (
                                <MenuItem dense disableRipple sx={{ cursor: "default", flexDirection: "column", alignItems: "flex-start", "&:hover": { bgcolor: "transparent" } }}>
                                    <Typography variant="caption" color="text.secondary">{t("nav.signedInAs")}</Typography>
                                    <Typography variant="caption" noWrap sx={{ fontWeight: "bold" }}>{current_email}</Typography>
                                </MenuItem>
                            )}
                            {current_email && <Divider />}

                            {current_email && <Divider />}
                            {current_email && (
                                <MenuItem dense onClick={() => router.delete("/logout")}>
                                    <ListItemIcon>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText slotProps={{ primary: { variant: "body2" } }}>{t("nav.logout")}</ListItemText>
                                </MenuItem>
                            )}
                        </Menu>
                    </Toolbar>
                </Container>
            </AppBar>
            {children}
            <FlashMessages />
            <Box component="footer" sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                    &copy;{new Date().getFullYear()} Gammes
                </Typography>
            </Box>
            </LocalizationProvider>
        </ThemeProvider>
    )
}
